import {Breakpoints, ThemeOptions} from '@mui/material';
import '@mui/material/Paper';

import {Tokens} from '../../../../types/generated/Tokens';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    messageCenter: true;
  }
}

export const MuiPaper = (
  {card, actionCard, selectableCard, clickableCard, focus, messageCenter}: Tokens,
  mediaQueries: Breakpoints
): ThemeOptions['components'] => {
  const {base, container} = card;
  const {backdrop} = actionCard;
  return {
    MuiPaper: {
      variants: [
        {
          props: {variant: 'messageCenter'},
          style: {
            // overrides MUI default background color for Message Center
            backgroundColor: messageCenter.desktop.background.color
          }
        }
      ],
      styleOverrides: {
        root: {
          '&.Material-baseCard': {
            maxWidth: '100%',
            width: '100%',
            padding: base.desktop.padding,
            [mediaQueries.down('sm')]: {
              padding: base.mobile.padding
            },
            position: 'relative',
            overflow: 'visible',
            '&.backdrop': {
              padding: backdrop.desktop.padding.all,
              borderTopRightRadius: 0,
              borderBottomLeftRadius: backdrop.desktop.border.radius.all,
              borderTopLeftRadius: 0,
              borderBottomRightRadius: backdrop.desktop.border.radius.all,
              [mediaQueries.down('sm')]: {
                padding: base.mobile.padding,
                borderBottomLeftRadius: base.mobile.border.radius,
                borderBottomRightRadius: base.mobile.border.radius
              }
            },
            '&.hasGutterBottom': {
              marginBottom: container.desktop.margin.bottom,
              [mediaQueries.down('sm')]: {
                marginBottom: container.mobile.margin.bottom
              },
              '&:last-child': {
                marginBottom: 0
              }
            },
            '&.clickable': {
              '&:hover': {
                backgroundColor: clickableCard.desktop.hover.background.color
              },
              '& .hasEndAdornment': {
                color: clickableCard.chevron.desktop.icon.color,
                height: clickableCard.chevron.desktop.height,
                width: clickableCard.chevron.desktop.width
              },
              '& .clickableCardTitle': {
                color: clickableCard.title.desktop.font.color
              }
            },
            '&.dashedBorder': {
              border: 0,
              backgroundPosition: 'right',
              backgroundImage:
                "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='%23333' stroke-width='1' stroke-dasharray='10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\")"
            },
            '&.selectable': {
              '& .MuiFormControlLabel-root:active, & .MuiFormControlLabel-root:focus, & .MuiFormControlLabel-root:focus-within':
                {
                  outline: 'none' // override the theme style
                },
              '& .MuiFormControlLabel-root': {
                margin: 0,
                marginBottom: 4,
                padding: 0,
                width: '100%',
                [mediaQueries.down('sm')]: {
                  alignItems: 'flex-start'
                }
              },
              '& .MuiFormControlLabel-label': {
                margin: 0,
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 'unset'
              },
              '&:hover': {
                backgroundColor: selectableCard.desktop.hover.background.color
              }
            },
            '& .hasOverlay': {
              position: 'initial',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                borderRadius: base.desktop.border.radius
              },
              '&.Mui-focusVisible:before': {
                outline: `${focus.outline.width} ${focus.outline.style} ${focus.outline.color}`
              },
              '&.MuiLink-root.Mui-focusVisible, &.MuiButtonBase-root.Mui-focusVisible': {
                outline: 0
              },
              '&.MuiLink-root.Mui-focusVisible': {
                textDecoration: 'underline'
              },
              '& > .MuiTouchRipple-root': {
                borderRadius: base.desktop.border.radius,
                [mediaQueries.down('sm')]: {
                  borderRadius: base.mobile.border.radius
                }
              },
              '&.MuiButtonBase-root': {
                wordBreak: 'break-word'
              }
            },
            '& .isPreferredChip.MuiChip-root .MuiChip-icon': {
              marginLeft: 0
            },
            '&.highlightCard': {
              backgroundColor: base.desktop.backgrounds.color.highlight
            },
            '&.defaultCard': {
              backgroundColor: base.desktop.backgrounds.color.white
            },
            '&.shadedCard': {
              backgroundColor: base.desktop.backgrounds.color.shaded
            },
            '&.shadedBackdropCard': {
              backgroundColor: base.desktop.backgrounds.color.backdropShaded
            },
            '&.transparentCard': {
              backgroundColor: base.desktop.backgrounds.color.transparent
            }
          }
        },
        /* Styles applied to the root element if `square={false}`. */
        rounded: {
          '&.Material-baseCard': {
            borderRadius: base.desktop.border.radius,
            [mediaQueries.down('sm')]: {
              borderRadius: base.mobile.border.radius
            },
            '&.xsmallBorderRadius': {
              borderRadius: container.desktop.sizing.border.radius.xs,
              padding: container.desktop.sizing.padding.xs,
              [mediaQueries.down('sm')]: {
                borderRadius: container.mobile.sizing.border.radius.xs,
                padding: container.mobile.sizing.padding.xs
              }
            },
            '&.smallBorderRadius': {
              borderRadius: container.desktop.sizing.border.radius.sm,
              padding: container.desktop.sizing.padding.sm,
              [mediaQueries.down('sm')]: {
                borderRadius: container.mobile.sizing.border.radius.sm,
                padding: container.mobile.sizing.padding.sm
              }
            },
            '&.mediumBorderRadius': {
              borderRadius: container.desktop.sizing.border.radius.md,
              padding: container.desktop.sizing.padding.md,
              [mediaQueries.down('sm')]: {
                borderRadius: container.mobile.sizing.border.radius.md,
                padding: container.mobile.sizing.padding.md
              }
            },
            '&.largeBorderRadius': {
              borderRadius: container.desktop.sizing.border.radius.lg,
              padding: container.desktop.sizing.padding.lg,
              [mediaQueries.down('sm')]: {
                borderRadius: container.mobile.sizing.border.radius.lg,
                padding: container.mobile.sizing.padding.lg
              }
            },
            '&.xlargeBorderRadius': {
              borderRadius: container.desktop.sizing.border.radius.xl,
              [mediaQueries.down('sm')]: {
                borderRadius: container.mobile.sizing.border.radius.xl,
                padding: container.mobile.sizing.padding.xl
              }
            },
            '&.zeroPadding': {
              padding: 0
            }
          }
        },
        outlined: {
          // overrides MUI default border rule
          borderColor: base.desktop.border.color, // '#B1B2B4',
          borderStyle: 'solid',
          borderWidth: 1
        },
        elevation2: {
          '&.Material-baseCard.clickable, &.Material-baseCard.selectable': {
            boxShadow: selectableCard.desktop.boxShadow
          }
        }
      }
    }
  };
};
